import * as React from 'react'
import Link from 'next/link'

import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import { useUser } from 'components/hooks/state'

import PreEnrolledCard from 'components/atomic/molecules/pre-enrolled-card'
import Button from 'components/atomic/atoms/button'
import Text from 'components/atomic/atoms/text'

import { PathGoal, PathOverview, TrackOverview } from 'types/types'
import * as PathUtils from 'utils/paths'
import Routes from 'utils/routes'

interface PreEnrollmentsProps {
    paths: PathOverview[]
    courses: TrackOverview[]
    showAll?: boolean
}

export default React.memo((props: PreEnrollmentsProps) => {
    const { t }: I18n = useTranslation()
    const { user } = useUser()

    const preEnrolledPaths = React.useMemo(() => {
        return props.paths.filter((path) => user?.pre_enrolled_paths?.includes(path._id))
    }, [user, props.paths])

    const preEnrolledCourses = React.useMemo(() => {
        return props.courses.filter((course) => user?.pre_enrolled_tracks?.includes(course._id))
    }, [user, props.courses])

    const items = React.useMemo(() => {
        return [
            ...preEnrolledPaths.map((p) => ({
                ...p,
                type: p.goal,
                to: Routes.Path(p.slug),
                enrolled_users: PathUtils.computeUserCount(p),
            })),
            ...preEnrolledCourses.map((c) => ({
                ...c,
                type: 'course',
                to: Routes.Course(c.slug),
                enrolled_users: PathUtils.computeUserCount(c),
            })),
        ].sort((a, b) => (a.updated_at > b.updated_at ? -1 : 1))
    }, [preEnrolledPaths, preEnrolledCourses])

    if (!items.length) return null

    const visibleItems = props.showAll ? items.length : 2

    return (
        <Stack gap={2}>
            <Stack gap={2} justifyContent="space-between" alignItems="center" direction="row">
                <Text variant="h5">{t('catalog:collections.preEnrolled')}</Text>
                {visibleItems < items.length && (
                    <Link key={Routes.CatalogPersonal} href={Routes.CatalogPersonal} passHref={true}>
                        <Button sx={{ flex: 'none' }}>{t('catalog:buttons.showAll')}</Button>
                    </Link>
                )}
            </Stack>
            <Grid spacing={2} container>
                {items.slice(0, visibleItems).map(({ type, ...item }) => (
                    <Grid item sm md={6} key={item._id}>
                        <PreEnrolledCard type={type as PathGoal} {...item} />
                    </Grid>
                ))}
            </Grid>
        </Stack>
    )
})
